import {debounce, getConfig, once, setBusy} from './core.js';
import {bindShowAll, bindStockToggle, isStockOnly, updateHiddenSummary} from './color-stock-filter.js';

let multiCartTrigger = null;

export function requestMultiCartDialog(trigger, event) {
	multiCartTrigger = trigger;
	document.body.dataset.openCartDialog = 'true';
	event.preventDefault();
	event.stopPropagation();
	window.naja.uiHandler.clickElement(trigger, {history: false}, event).catch((error) => {
		delete document.body.dataset.openCartDialog;
		console.error('Cart dialog could not be loaded.', error);
	});
}

function formatNumber(value, fractionDigits, config) {
	const sign = value < 0 ? '-' : '';
	const [integer, fraction = ''] = Math.abs(value).toFixed(fractionDigits).split('.');
	const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, config.thousandSeparator || ' ');
	return `${sign}${grouped}${fraction ? (config.decimalSeparator || ',') + fraction : ''}`;
}

function formatCurrency(value, config) {
	const formatted = formatNumber(value, 2, config);
	const currency = config.cartSummary?.currency || '';
	return config.cartSummary?.currencyPosition === 'pred' ? `${currency} ${formatted}` : `${formatted} ${currency}`;
}

function setMiniCartOpen(open) {
	const panel = document.getElementById('cart_block');
	const toggle = document.querySelector('[data-mini-cart-toggle]');
	if (!panel || !toggle) {
		return;
	}
	document.body.dataset.miniCartOpen = String(open);
	panel.hidden = !open;
	panel.classList.toggle('is-open', open);
	toggle.setAttribute('aria-expanded', String(open));
}

function updateMiniCartLoadingState() {
	const spinner = document.getElementById('cart-spinner');
	if (spinner && document.querySelector('#cart_block_list tbody tr')) {
		spinner.hidden = true;
	}
}

function bindMiniCart() {
	const toggle = document.querySelector('[data-mini-cart-toggle]');
	if (toggle && once(toggle, 'mini-cart-toggle')) {
		toggle.addEventListener('click', (event) => {
			event.preventDefault();
			const open = toggle.getAttribute('aria-expanded') !== 'true';
			setMiniCartOpen(open);
			if (open) {
				window.naja.makeRequest('GET', toggle.href, null, {history: false}).catch(console.error);
			}
		});
	}
	if (once(document.documentElement, 'mini-cart')) {
		document.addEventListener('click', (event) => {
			if (event.target.closest?.('[data-mini-cart-toggle]')) {
				return;
			}
			if (!event.target.closest?.('#cart_block')) {
				setMiniCartOpen(false);
			}
		});
		document.addEventListener('keydown', (event) => {
			if (event.key === 'Escape' && document.body.dataset.miniCartOpen === 'true') {
				setMiniCartOpen(false);
				document.querySelector('[data-mini-cart-toggle]')?.focus();
			}
		});
	}
	setMiniCartOpen(document.body.dataset.miniCartOpen === 'true');
	updateMiniCartLoadingState();
}

function updateMultiCart(dialog) {
	let count = 0;
	dialog.querySelectorAll('[data-color-quantity]').forEach((input) => {
		count += Math.max(0, Number.parseInt(input.value, 10) || 0);
	});
	const submit = dialog.querySelector('button[type="submit"]');
	if (submit) {
		submit.disabled = count === 0;
		submit.classList.toggle('disabled', count === 0);
	}
	const sum = dialog.querySelector('[data-cart-sum]');
	if (sum) {
		const config = getConfig();
		const unitPrice = Number.parseFloat(sum.dataset.price || '0');
		const unitWeight = Number.parseFloat(sum.dataset.weight || '0');
		sum.querySelector('[data-cart-sum-label]').textContent = `${config.cartSummary?.total || ''}:`;
		sum.querySelector('[data-cart-sum-value]').textContent = formatCurrency(count * unitPrice, config);
		sum.querySelector('[data-cart-sum-detail]').textContent = config.cartSummary?.showWeight
			? ` (${formatNumber(count * unitWeight, 0, config)} g)`
			: ` (${config.cartSummary?.quantity || ''}: ${count})`;
	}
}

function filterMultiCart(dialog) {
	const query = (dialog.querySelector('[data-color-quantity-search]')?.value || '').trim().toLocaleLowerCase();
	let hiddenCount = 0;
	dialog.querySelectorAll('[data-color-quantity-row]').forEach((row) => {
		const matchesSearch = query === '' || row.dataset.colorSearch.includes(query);
		const hiddenByStock = matchesSearch && isStockOnly() && row.dataset.orderable !== 'true';
		row.hidden = !matchesSearch || hiddenByStock;
		if (hiddenByStock) {
			hiddenCount += 1;
		}
	});
	updateHiddenSummary(dialog, hiddenCount);
}

function bindDialog(dialog) {
	if (!once(dialog, 'multi-cart')) {
		return;
	}
	const search = dialog.querySelector('[data-color-quantity-search]');
	dialog.querySelectorAll('[data-color-stock-toggle]').forEach(bindStockToggle);
	dialog.querySelectorAll('[data-color-stock-show-all]').forEach(bindShowAll);
	search?.addEventListener('input', debounce(() => filterMultiCart(dialog)));
	dialog.addEventListener('color-stock-filter:change', () => filterMultiCart(dialog));
	dialog.addEventListener('input', (event) => {
		if (event.target.matches('[data-color-quantity]')) {
			updateMultiCart(dialog);
		}
	});
	const modal = dialog.closest('.modal');
	if (modal && once(modal, 'multi-cart-focus')) {
		window.jQuery?.(modal).on('hidden.bs.modal', () => {
			if (multiCartTrigger && document.contains(multiCartTrigger)) {
				multiCartTrigger.focus();
			}
			multiCartTrigger = null;
		});
	}
	filterMultiCart(dialog);
	updateMultiCart(dialog);
}

function bindMultiCartAjax() {
	if (!once(document.documentElement, 'multi-color-cart-naja')) {
		return;
	}
	window.naja.addEventListener('start', ({detail: {options}}) => {
		const trigger = options.windeaElement;
		if (trigger?.matches?.('.cart-add.ajax')) {
			setBusy(trigger, true);
		}
	});
	window.naja.addEventListener('success', ({detail: {payload, options}}) => {
		const trigger = options.windeaElement;
		if (payload.showModal === '#dlgAddToCart') {
			delete document.body.dataset.openCartDialog;
		}
		if (trigger?.closest?.('#dlgAddToCart') && payload.snippets?.['snippet--miniCart'] && !payload.alertModal) {
			window.jQuery('#dlgAddToCart').modal('hide');
		}
	});
	window.naja.addEventListener('error', ({detail: {options}}) => {
		if (options.windeaElement?.matches?.('.cart-add.ajax')) {
			delete document.body.dataset.openCartDialog;
		}
	});
	window.naja.addEventListener('complete', ({detail: {options}}) => {
		if (options.windeaElement?.matches?.('.cart-add.ajax')) {
			setBusy(options.windeaElement, false);
		}
	});
}

export function init(root = document) {
	bindMiniCart();
	bindMultiCartAjax();
	root.querySelectorAll('[data-multi-color-cart]').forEach(bindDialog);
	if (once(document.documentElement, 'cart-dialog-trigger')) {
			document.addEventListener('click', (event) => {
				const trigger = event.target.closest?.('.cart-add.ajax');
				if (!trigger) {
					return;
				}
				if (trigger.matches('.autocomplete-result__cart')) {
					return;
				}
				multiCartTrigger = trigger;
				document.body.dataset.openCartDialog = 'true';
			});
	}

}
